import React, { useEffect, useState, useCallback } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../utils/api';
import { getCurrentUser } from '../../utils/auth';
import { FaSearch, FaEdit, FaTrash, FaInfoCircle } from 'react-icons/fa';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import UserInfoModal from '../../components/common/UserInfoModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';

const USERS_PER_PAGE = 5;

export default function AdminUsersPage() {
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isSupplierAdmin = currentUser?.role === 'supplieradmin';
  const currentUserId = currentUser?._id || currentUser?.userId;

  // State
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [selectedSupplierId, setSelectedSupplierId] = useState(
    isSupplierAdmin ? currentUser?.supplierId : ''
  );
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [currentSupplier, setCurrentSupplier] = useState(null);

  const indexOfLastUser = currentPage * USERS_PER_PAGE;
  const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users');
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(list);
      setFilteredUsers(list);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/api/suppliers');
      const list = Array.isArray(res.data) ? res.data : [];
      setSuppliers(list);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      toast.error(err.response?.data?.message || 'Failed to load suppliers.');
    }
  }, [isSuperAdmin]);

  // Fetch current supplier for supplier admins
  useEffect(() => {
    if (!isSuperAdmin) {
      api.get('/api/suppliers/my-suppliers')
        .then(res => {
          if (res.data && res.data.length > 0) {
            setCurrentSupplier(res.data[0]);
          }
        })
        .catch(err => console.error('Failed to fetch my supplier', err));
    }
  }, [isSuperAdmin]);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
    fetchSuppliers();
  }, [fetchUsers, fetchSuppliers]);

  // Search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const lower = query.toLowerCase();
      setFilteredUsers(users.filter((u) => u.username?.toLowerCase().includes(lower)));
      setCurrentPage(1);
    },
    [users]
  );

  useEffect(() => {
    if (searchQuery === '') setFilteredUsers(users);
  }, [users, searchQuery]);

  // Reset form
  const resetForm = () => {
    setName('');
    setEmail('');
    setRole('member');
    setSelectedSupplierId(isSupplierAdmin ? currentUser?.supplierId : '');
    setEditingUser(null);
  };

  // Submit user form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error('Username and email are required.');
      return;
    }
  // ⚠️ Validation before sending to backend
  if (
    (role === 'supplieradmin' || role === 'member') &&
    !(
      isSuperAdmin ? selectedSupplierId : currentUser?.supplierId
    )
  ) {
    toast.error('Supplier ID is required for this role. Please select a supplier.');
    return;
  }

    const payload = {
      username: name.trim(),
      email: email.trim(),
      role,
      password: '', // backend generates/reset password
      autoApprove: role !== 'member',
    };
   // Only set supplierId if the role is not superadmin
   if (role !== 'superadmin') {
      payload.supplierId = isSuperAdmin ? selectedSupplierId : currentUser?.supplierId;
    }
console.error('Submitting payload:', payload);

    try {
      let res;
      if (editingUser) {
        res = await api.patch(`/api/users/${editingUser._id}`, payload);
        const updated = res.data;
        toast.success(`User "${updated.user.username}" updated!`);
        setUsers(prev => prev.map(u => (u._id === updated._id ? updated : u)));
        setFilteredUsers(prev => prev.map(u => (u._id === updated._id ? updated : u)));
      } else {
        res = await api.post('/api/users/admin-create', payload);
        const created = res.data;
        toast.success(`User "${created.user.username}" added! Reset password sent to ${created.user.email}`);
        setUsers(prev => [created, ...prev]);
        setFilteredUsers(prev => [created, ...prev]);
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error('Save user failed:', err);
      toast.error(err.response?.data?.message || 'Failed to save user.');
    }
  };

  // Edit user
  const handleEdit = (user) => {
    if (user._id === currentUserId) return;
    setEditingUser(user);
    setName(user.username);
    setEmail(user.email || '');
    setRole(user.role || 'member');
    setSelectedSupplierId(user.supplierId || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete user
  const handleDeleteClick = (user) => {
    if (user._id === currentUserId) return;
    setUserToDelete(user);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/api/users/${userToDelete._id}`);
      setUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      setFilteredUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      toast.success(`User "${userToDelete.username}" deleted.`);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.message || 'Failed to delete user.');
    } finally {
      setShowConfirm(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setUserToDelete(null);
  };

  const openUserInfoModal = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const openSupplierInfoModal = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-5xl mx-auto bg-white shadow p-6 rounded-md">
        <h2 className="text-2xl font-bold mb-4">Admin - Manage Users</h2>

        {/* Search */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search users by name"
              className="border p-2 w-full pr-10 rounded"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
              <FaSearch />
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        <form onSubmit={handleSubmit} className="space-y-2 mb-6 bg-gray-50 p-4 rounded">
          <h3 className="text-lg font-semibold">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Username"
                className="border p-2 w-full rounded"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="border p-2 w-full rounded"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="border p-2 w-full rounded"
              >
                {isSuperAdmin && <option value="superadmin">Super Admin</option>}
                <option value="supplieradmin">Supplier Admin</option>
                <option value="member">Member</option>
              </select>
            </div>

            {/* Supplier */}
          <div>
  <label className="block text-sm font-medium mb-1">Supplier</label>
  {isSuperAdmin ? (
    <select
      value={selectedSupplierId}
      onChange={(e) => setSelectedSupplierId(e.target.value)}
      className="border p-2 w-full rounded"
      // remove 'required' so superadmin can leave it blank
    >
      <option value="">-- All suppliers (optional) --</option>
      {suppliers.map((s) => (
        <option key={s._id} value={s._id}>{s.name}</option>
      ))}
    </select>
  ) : (
    <input
      type="text"
      value={currentSupplier?.name || ''}
      disabled
      className="border p-2 w-full rounded bg-gray-100"
    />
  )}
</div>

          </div>

          {/* Form Buttons */}
          <div className="flex space-x-2 mt-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={loading || editingUser?._id === currentUserId}
            >
              {editingUser ? 'Update User' : 'Add User'}
            </button>
            {editingUser && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <thead>
              <tr className="bg-gray-200">
                <th className="border p-2 text-left">Username</th>
                <th className="border p-2 text-left">Email</th>
                <th className="border p-2 text-left">Role</th>
                <th className="border p-2 text-left">Supplier</th>
                <th className="border p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="border p-4 text-center">Loading users...</td>
                </tr>
              ) : currentUsers.length > 0 ? (
                currentUsers.map((user) => {
                  const supplier =
                    typeof user.supplierId === 'object'
                      ? user.supplierId
                      : suppliers.find((s) => s._id === user.supplierId);

                  const isCurrentUser = user._id === currentUserId;

                  return (
                    <tr key={user._id} className={`text-center border-t hover:bg-gray-50 ${isCurrentUser ? 'bg-gray-100 text-gray-400' : ''}`}>
                      <td className="border p-2 text-left">
                        <button
                          onClick={() => openUserInfoModal(user)}
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          {user.username}
                        </button>
                      </td>
                      <td className="border p-2 text-left">{user.email || '-'}</td>
                      <td className="border p-2 text-left">{user.role}</td>
                      <td className="border p-2 text-left">
                        {supplier ? (
                          <button
                            onClick={() => openSupplierInfoModal(supplier)}
                            className="text-blue-600 underline hover:text-blue-800"
                          >
                            {supplier.name}
                          </button>
                        ) : isSupplierAdmin && currentSupplier ? (
                          <button
                            onClick={() => openSupplierInfoModal(currentSupplier)}
                            className="text-blue-600 underline hover:text-blue-800"
                          >
                            {currentSupplier.name}
                          </button>
                        ) : (
                          <button disabled className="text-gray-400 cursor-not-allowed">—</button>
                        )}
                      </td>
                      <td className="border p-2 space-x-2">
                        <button
                          onClick={() => handleEdit(user)}
                          disabled={isCurrentUser}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded text-white ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-500'}`}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          disabled={isCurrentUser}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded text-white ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600'}`}
                        >
                          <FaTrash /> Delete
                        </button>
                        <button
                          onClick={() => openUserInfoModal(user)}
                          className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded"
                        >
                          <FaInfoCircle /> Info
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="border p-4 text-center text-gray-600">
                    {users.length === 0 ? 'No users available.' : 'No users match the search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center space-x-2 mt-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded border ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* Modals */}
        <UserInfoModal user={selectedUser} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
        <SupplierInfoModal supplier={selectedSupplier} isOpen={supplierModalOpen} onClose={() => setSupplierModalOpen(false)} />
        <ConfirmationModal
          isOpen={showConfirm}
          title="Confirm Delete"
          message={`Are you sure you want to delete user "${userToDelete?.username}"? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onClose={cancelDelete}
          username={userToDelete?.username}
        />

        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      </div>
    </div>
  );
}
